/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {
  getAccountId,
  getGridColumnState,
  onSetGridColumns,
  onUpdateGridColumns
} from '@ion/applications/src/helpers';
import {error} from '@ion/core/src/logger/logger';
import {loadJobStatus} from '@ion/core/src/services/Connect';
import {JobStatus} from '@ion/core/src/types/entities';
import {
  LoadjobStatusParams,
  ServiceError
} from '@ion/core/src/types/services';
import {AsyncState, PageInitializeAction} from '@/types/redux';

// defines the user preference key for the jobStatus page ...
const PREFERENCE                       = 'jobStatus';

// name of the reducer
export const NAME                      = 'jobStatus';

/**
 * ACTIONS
 */
export const INITIALIZE_JOB_STATUS      = 'INITIALIZE_JOB_STATUS';
export const INITIALIZE_TASK_STATUS     = 'INITIALIZE_TASK_STATUS';
export const SET_GRID_COLUMNS           = 'SET_GRID_COLUMNS';
export const SET_JOB_STATUS_OPEN        = 'SET_JOB_STATUS_OPEN';
export const SET_TASK_GRID_COLUMNS      = 'SET_TASK_GRID_COLUMNS';
export const UPDATE_TASK_GRID_COLUMNS   = 'UPDATE_TASK_GRID_COLUMNS';
export const UPDATE_GRID_COLUMNS        = 'UPDATE_GRID_COLUMNS';
export const JOB_STATUS_GRID_REFRESHED  = 'JOB_STATUS_GRID_REFRESHED';
export const GET_JOB_STATUS_REQUEST     = 'GET_JOB_STATUS_REQUEST';
export const GET_JOB_STATUS_RESPONSE    = 'GET_JOB_STATUS_RESPONSE';
export const GET_JOB_STATUS_ERROR       = 'GET_JOB_STATUS_ERROR';
export const SHOW_JOB_STATUS            = 'SHOW_JOB_STATUS';
export const GET_TASK_STATUS_REQUEST    = 'GET_TASK_STATUS_REQUEST';
export const GET_TASK_STATUS_RESPONSE   = 'GET_TASK_STATUS_RESPONSE';
export const SET_TASK_STATUS_RESPONSE   = 'SET_TASK_STATUS_RESPONSE';
export const GET_TASK_STATUS_ERROR      = 'GET_TASK_STATUS_ERROR';

/**
 * initialize jobStatus
 */
export const initializeJobStatus = (): PageInitializeAction => ({
  type : INITIALIZE_JOB_STATUS
});

/**
 * initialize jobStatus
 */
export const initializeTaskStatus = (): PageInitializeAction => ({
  type : INITIALIZE_TASK_STATUS
});

/**
 * set Grid Columns
 */
export const setGridColumns = (columnState: ColumnState[]) => {
  return {type: SET_TASK_GRID_COLUMNS, columnState};
};

/**
 * opens up the job status module
 */
export const setJobStatus = (state: boolean) => ({
  type : SET_JOB_STATUS_OPEN, open : state
});

/**
 * jobStatus Grid Refreshed
 */
export const jobStatusGridRefreshed = (): PageInitializeAction => ({
  type : JOB_STATUS_GRID_REFRESHED
});

/**
 * getJobStatus return all tasks
 */
export const getJobStatus = ({
  pageId = 1,
  pageSize = 20,
  sort,
  filter = {},
  includeSubGroups = false,
  callback: callbackFn
}) => {
  return async(dispatch: Function, getState: Function): Promise<any> => {
    dispatch({type: GET_JOB_STATUS_REQUEST});
    const data: LoadjobStatusParams = {
      accountId : getAccountId(getState),
      pageId,
      pageSize,
      includeSubGroups
    };
    if (sort) {
      data.sortOn = sort;
    }
    if (filter) {
      data.filterOn = filter;
    }
    try {
      const {items, count: totalCount} = await loadJobStatus(data);
      dispatch({type: GET_JOB_STATUS_RESPONSE});
      callbackFn?.({items, totalCount});
      return {items, totalCount};
    } catch (err) {
      error(err);
      dispatch({type: GET_JOB_STATUS_ERROR, error: err});
      return err;
    }
  };
};

export const getFeatureName = (features) => {
  const [key, val] = Object.entries(features)[0];
  return `${key} - ${val}`;
};

export const showJobStatus = (taskStatus: JobStatus, show = true) => ({
  type : SHOW_JOB_STATUS, taskStatus, show
});

interface getJobStatus {
  items: JobStatus | JobStatus[] | null;
}

/**
 * REDUCERS
 */

export interface JobStatusState extends AsyncState {
  loading: boolean,
  isInitialized: boolean,
  error: ServiceError,
  jobstatus: {
    error: ServiceError;
    loading: boolean;
  },
  open: boolean,
  showTaskStatus: boolean,
  taskStatus: JobStatus,
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean
  },
  taskGrid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean
  },
  getJobStatus: getJobStatus
}

// defines the initial state for the reducer ...
export const initialState: JobStatusState = {
  open           : false,
  isInitialized  : false,
  loading        : false,
  error          : null,
  showTaskStatus : false,
  taskStatus     : null,
  jobstatus      : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false
  },
  taskGrid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false
  },
  getJobStatus : {
    items : null
  }
};

// defines the notifications reducers ...
const reducers = {
  [INITIALIZE_JOB_STATUS] : draft => {
    draft.isInitialized                = true;
    const preference                   = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState             = preference;
    draft.grid.preferedColumnState     = preference;
  },
  [INITIALIZE_TASK_STATUS] : draft => {
    draft.isInitialized                = true;
    const preference                   = getGridColumnState(PREFERENCE, true);
    draft.taskGrid.columnState         = preference;
    draft.taskGrid.preferedColumnState = preference;
  },
  [SET_TASK_STATUS_RESPONSE] : ({getJobStatus}: JobStatusState, {items}) => {
    getJobStatus.items = items;
  },
  [SET_JOB_STATUS_OPEN] : (draft: JobStatusState, {open}) => {
    draft.open = open;
  },
  [SET_TASK_GRID_COLUMNS] : ({taskGrid, jobstatus}: JobStatusState, {columnState}) => {
    onSetGridColumns({
      grid       : taskGrid,
      isLoading  : jobstatus.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_TASK_GRID_COLUMNS] : ({taskGrid}: JobStatusState, {columnState}) => {
    onUpdateGridColumns({grid: taskGrid, preference: PREFERENCE}, columnState);
  },
  [SET_GRID_COLUMNS] : ({grid, jobstatus}: JobStatusState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : jobstatus.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_GRID_COLUMNS] : ({grid}: JobStatusState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [SHOW_JOB_STATUS] : (draft: JobStatusState, {taskStatus, show}) => {
    draft.showTaskStatus = show;
    draft.taskStatus = taskStatus;
  }
};

// defines all reducers for actions of interest to the this reducer ...
export default produce((draft: JobStatusState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
